import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import '../../styles/landing.css';

const About = () => {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const nav = document.querySelector('.landing-nav');
        const handleScroll = () => {
            if (window.scrollY > 50) {
                nav?.classList.add('scrolled');
            } else {
                nav?.classList.remove('scrolled');
            }
        };
        window.addEventListener('scroll', handleScroll);

        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.1
        };

        const observer = new IntersectionObserver((entries, obs) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('in-view');
                    obs.unobserve(entry.target);
                }
            });
        }, observerOptions);

        document.querySelectorAll('.scroll-animate').forEach((element) => {
            observer.observe(element);
        });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            observer.disconnect();
        };
    }, []);

    return (
        <div className="landing-body" style={{ paddingTop: '80px' }}>
            <Helmet>
                <title>Envion | Workforce Readiness Platform</title>
                <meta name="description" content="Envion exists to better connect businesses with their people. Learn about our mission to transform employee onboarding through AI-powered intelligent pathways." />
                <link rel="canonical" href="https://envion.org.uk/about" />
                <meta property="og:title" content="Envion" />
                <meta property="og:description" content="Envion exists to better connect businesses with their people. Learn about our mission to transform employee onboarding through AI-powered intelligent pathways." />
                <meta property="og:image" content="Will be provided" />
                <meta property="og:url" content="https://envion.org.uk/about" />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="Envion" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Envion" />
                <meta name="twitter:description" content="Envion exists to better connect businesses with their people. Learn about our mission to transform employee onboarding through AI-powered intelligent pathways." />
                <meta name="twitter:image" content="Will be provided" />
            </Helmet>
            <style>
                {`
                    .about-section {
                        padding: 120px 40px;
                    }
            
                    .about-content {
                        max-width: 1200px;
                        margin: 0 auto;
                    }
            
                    .story-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 80px;
                        align-items: center;
                    }
            
                    .vision-mission-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 40px;
                        margin-top: 80px;
                    }
            
                    .vision-card {
                        background: white;
                        padding: 60px;
                        border-radius: 32px;
                        border: 1px solid var(--lp-border);
                        transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
                    }
            
                    .vision-card:hover {
                        transform: translateY(-8px);
                        box-shadow: 0 40px 80px rgba(0, 0, 0, 0.05);
                        border-color: var(--lp-primary-orange);
                    }
            
                    .values-grid-container {
                        max-width: 1200px;
                        margin: 0 auto;
                    }
            
                    .values-grid-header {
                        margin-bottom: 80px;
                        text-align: left;
                    }
            
                    .values-subtitle {
                        font-size: 1.25rem;
                        color: var(--lp-text-muted);
                        line-height: 1.6;
                        max-width: 600px;
                        margin: 0;
                    }
            
                    .values-grid-body {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                    }
            
                    .value-grid-item {
                        padding: 60px;
                        transition: all 0.3s ease;
                    }
            
                    /* Grid lines */
                    .value-grid-item:nth-child(1) {
                        border-right: 1px solid rgba(42, 63, 84, 0.15);
                        border-bottom: 1px solid rgba(42, 63, 84, 0.15);
                    }
            
                    .value-grid-item:nth-child(2) {
                        border-bottom: 1px solid rgba(42, 63, 84, 0.15);
                    }
            
                    .value-grid-item:nth-child(3) {
                        border-right: 1px solid rgba(42, 63, 84, 0.15);
                    }
            
                    .value-text-box h4 {
                        font-size: 1.5rem;
                        font-weight: 800;
                        color: var(--lp-secondary-navy);
                        margin-bottom: 16px;
                    }
            
                    .value-text-box p {
                        font-size: 1.15rem;
                        color: var(--lp-text-muted);
                        line-height: 1.8;
                        margin: 0;
                    }
            
                    .story-text p {
                        font-size: 1.15rem;
                        color: var(--lp-text-muted);
                        line-height: 1.8;
                        margin-bottom: 24px;
                    }
            
                    .story-text h2 {
                        font-size: 2.5rem;
                        color: var(--lp-secondary-navy);
                        margin-bottom: 32px;
                        font-weight: 800;
                    }
            
                    .mission-header {
                        text-transform: uppercase;
                        font-size: 0.85rem;
                        font-weight: 800;
                        color: var(--lp-primary-orange);
                        letter-spacing: 0.1em;
                        margin-bottom: 16px;
                        display: block;
                    }
            
                    @media (max-width: 968px) {
                        .values-grid-body {
                            grid-template-columns: 1fr;
                        }
            
                        .value-grid-item {
                            padding: 40px 0;
                            border-right: none !important;
                            border-bottom: 1px solid rgba(42, 63, 84, 0.15) !important;
                        }
            
                        .value-grid-item:last-child {
                            border-bottom: none !important;
                        }
            
                        .story-grid,
                        .vision-mission-grid {
                            grid-template-columns: 1fr;
                            gap: 40px;
                        }

                        .about-section {
                            padding: 60px 20px !important;
                        }

                        .vision-card {
                            padding: 30px !important;
                        }

                        .values-grid-header h2 {
                            font-size: 2rem !important;
                        }
                    }
                `}
            </style>

            {/* Navigation */}
            <nav className={`landing-nav${menuOpen ? ' nav-open' : ''}`}>
                <div className="logo">
                    <Link to="/" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="fa-solid fa-layer-group logo-icon" style={{ color: 'var(--lp-primary-orange)' }}></i>
                        <span style={{ color: 'var(--lp-primary-orange)' }}>envion</span>
                    </Link>
                </div>
                <button className="hamburger-btn" onClick={() => setMenuOpen(!menuOpen)}>
                    <i className={menuOpen ? 'fa-solid fa-xmark' : 'fa-solid fa-bars'}></i>
                </button>
                <div className="nav-links">
                    <div className="nav-item-dropdown">
                        <Link to="/solutions" className="nav-link">Solutions <i className="fa-solid fa-chevron-down" style={{ fontSize: '0.7em', marginLeft: '6px', opacity: 0.7 }}></i></Link>
                        <div className="dropdown-menu">
                            <Link to="/solutions#use-cases" className="dropdown-item">
                                <div className="dropdown-content">
                                    <div className="dropdown-header">
                                        <strong>Preboarding</strong>
                                        <i className="fa-solid fa-arrow-right dropdown-arrow"></i>
                                    </div>
                                    <span>Engagement starts early.</span>
                                </div>
                            </Link>
                            <Link to="/solutions#use-cases" className="dropdown-item">
                                <div className="dropdown-content">
                                    <div className="dropdown-header">
                                        <strong>Onboarding</strong>
                                        <i className="fa-solid fa-arrow-right dropdown-arrow"></i>
                                    </div>
                                    <span>Accelerate ramp-up time.</span>
                                </div>
                            </Link>
                        </div>
                    </div>
                    <Link to="/about" className="nav-link" style={{ color: 'var(--lp-primary-orange)' }}>About Us</Link>
                    <Link to="/#how-it-works" className="nav-link">How it Works</Link>
                    <Link to="/contact" className="nav-link">Contact</Link>
                </div>
                <div className="auth-buttons">
                    <button onClick={() => navigate('/login')} className="btn-lp btn-lp-secondary w-full sm:w-auto text-center" style={{ cursor: 'pointer' }}>Log In</button>
                    <Link to="/contact" className="btn-lp btn-lp-primary">Get a Quote</Link>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="scroll-animate animate-fade-in" style={{ position: 'relative', padding: '140px 40px 100px', background: 'white', textAlign: 'center' }}>
                <div style={{ maxWidth: '1000px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
                    <span className="mission-header" style={{ justifyContent: 'center' }}>Our Mission</span>
                    <h1 className="hero-headline" style={{ fontSize: '4.5rem', fontWeight: 800, color: 'var(--lp-secondary-navy)', marginBottom: '32px', lineHeight: 1.1, letterSpacing: '-0.02em' }}>
                        Revolutionizing Employee Onboarding
                    </h1>
                    <p style={{ fontSize: '1.4rem', color: 'var(--lp-text-muted)', lineHeight: 1.7, fontWeight: 400, maxWidth: '850px', margin: '0 auto' }}>
                        Powerful technology should feel effortless. Our mission is to unite leadership and employees through
                        personalized onboarding experiences that drive real results — eliminating complexity while delivering
                        exceptional journeys at scale.
                    </p>
                </div>
            </section>

            {/* Our Story */}
            <section className="about-section" style={{ position: 'relative', background: 'var(--lp-bg-off-white)' }}>
                <div className="about-content" style={{ position: 'relative', zIndex: 2 }}>
                    <div className="story-grid">
                        <div className="story-text scroll-animate animate-slide-up">
                            <h2>Our Story</h2>
                            <p>
                                Founded in Manchester, envion was born from direct experience: employees are the foundation of
                                company success, yet onboarding — the moment that shapes everything — was being left to
                                improvisation and spreadsheets. Through our own ventures, we discovered the real challenge
                                wasn't hiring talent, it was bringing them onboard with the company-specific knowledge, culture,
                                and mindset they needed to thrive.
                            </p>
                            <p>
                                We set out to build a dedicated platform that puts structured employee experiences within reach
                                of every growing business. A platform where creating personalized journeys takes
                                minutes, not months, and where exceptional first impressions are designed in, not hoped for.
                            </p>
                            <p>
                                As a young company, we move fast. Customer feedback doesn't sit in backlogs — it becomes
                                implementations. We're building envion alongside our early partners, delivering bespoke
                                solutions tailored to how each organisation actually works, because your success defines ours.
                            </p>
                        </div>
                        <div className="scroll-animate animate-slide-up delay-100" style={{ background: 'var(--lp-secondary-navy)', borderRadius: '32px', padding: '48px', color: 'white', display: 'flex', flexDirection: 'column', gap: '40px', boxShadow: '0 40px 80px rgba(0,0,0,0.1)' }}>
                            {/* Mission Point */}
                            <div style={{ flex: 1 }}>
                                <i className="fa-solid fa-bullseye" style={{ fontSize: '1.5rem', color: 'var(--lp-primary-orange)', marginBottom: '20px', display: 'block' }}></i>
                                <h3 style={{ fontSize: '1.5rem', color: 'white', marginBottom: '12px', fontWeight: 800 }}>Our Mission</h3>
                                <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, margin: 0 }}>
                                    To democratise enterprise-grade onboarding orchestration. We are removing the complexity of
                                    fragmented workflows and manual tracking, delivering a unified, intelligent platform that
                                    achieves seamless interoperability with your existing ecosystem.
                                </p>
                            </div>

                            {/* Divider */}
                            <div style={{ height: '1px', background: 'rgba(255,255,255,0.15)', width: '100%' }}></div>

                            {/* Vision Point */}
                            <div style={{ flex: 1 }}>
                                <i className="fa-solid fa-eye" style={{ fontSize: '1.5rem', color: 'white', marginBottom: '20px', display: 'block' }}></i>
                                <h3 style={{ fontSize: '1.5rem', color: 'white', marginBottom: '12px', fontWeight: 800 }}>Our Vision</h3>
                                <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, margin: 0 }}>
                                    A world where organisations can onboard employees exceptionally and effortlessly —
                                    regardless of scale or resource constraints — eliminating the structural barriers that limit
                                    strategic velocity and the delivery of bespoke employee experiences.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Values Grid */}
            <section className="about-section" style={{ position: 'relative', background: 'white' }}>
                <div className="about-content" style={{ position: 'relative', zIndex: 2 }}>
                    <div className="values-grid-container">
                        <div className="values-grid-header scroll-animate animate-slide-up">
                            <h2 style={{ fontSize: '3rem', color: 'var(--lp-secondary-navy)', fontWeight: 800, lineHeight: 1.1, marginBottom: '24px' }}>
                                Our Values</h2>
                            <p className="values-subtitle">The core principles that guide our craft and the relationships we build at envion.</p>
                        </div>
                        <div className="values-grid-body">
                            {/* Value 1 */}
                            <div className="value-grid-item scroll-animate animate-slide-up">
                                <div className="value-text-box">
                                    <h4>Mutual Success</h4>
                                    <p>Your growth is the only metric that defines our own. We don't just provide software; we
                                        invest in the journeys of the people who use it.</p>
                                </div>
                            </div>
                            {/* Value 2 */}
                            <div className="value-grid-item scroll-animate animate-slide-up delay-100">
                                <div className="value-text-box">
                                    <h4>Responsive Evolution</h4>
                                    <p>We move at the speed of your feedback. Being close to our partners allows us to refine
                                        and adapt envion in real-time, ensuring we build exactly what you need.</p>
                                </div>
                            </div>
                            {/* Value 3 */}
                            <div className="value-grid-item scroll-animate animate-slide-up delay-200">
                                <div className="value-text-box">
                                    <h4>Architectural Integrity</h4>
                                    <p>Reliability isn't a feature; it's our foundation. We prioritize security and structural
                                        excellence so you can focus on scale without compromise.</p>
                                </div>
                            </div>
                            {/* Value 4 */}
                            <div className="value-grid-item scroll-animate animate-slide-up delay-300">
                                <div className="value-text-box">
                                    <h4>Operational Simplicity</h4>
                                    <p>Technology should empower, not overwhelm. We are dedicated to stripping away complexity
                                        to deliver effortless onboarding experiences that just work.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA Section */}
            <section className="cta-section" style={{ background: '#0f172a', padding: '100px 20px', textAlign: 'center', color: 'white' }}>
                <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                    <h2 style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: '20px', color: 'white', whiteSpace: 'nowrap' }}>
                        Ready to elevate your employee onboarding?</h2>
                    <p style={{ fontSize: '1.25rem', marginBottom: '40px', color: '#cbd5e1', lineHeight: 1.6 }}>
                        Partner with us to build a bespoke onboarding solution tailored to your organisation's needs.
                    </p>
                    <div className="cta-group" style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginBottom: '20px' }}>
                        <a href="https://outlook.office.com/book/DiscoveryCall@envion.org.uk/?ismsaljsauthenabled"
                            className="btn-lp btn-lp-primary" style={{ padding: '16px 32px', fontSize: '1.1rem' }} target="_blank"
                            rel="noopener noreferrer">Book a Meeting</a>
                        <Link to="/contact" className="btn-lp btn-lp-primary"
                            style={{ background: 'transparent', border: '1px solid white', color: 'white', padding: '16px 32px', fontSize: '1.1rem' }}>Get a Quote</Link>
                    </div>
                    <p style={{ fontSize: '0.9rem', color: '#94a3b8' }}>No commitment required. Free consultation included.</p>
                </div>
            </section>

            {/* Footer */}
            <footer className="site-footer">
                <div className="footer-content">
                    <div className="footer-brand">
                        <h2><i className="fa-solid fa-layer-group"></i> envion</h2>
                        <p className="footer-subtitle" style={{ fontSize: '1.1rem', color: 'white', lineHeight: 1.5, marginBottom: '24px' }}>
                            envion is a modern workforce readiness platform. Enabling faster, smarter onboarding and preboarding
                            for today’s organisations.
                        </p>
                        <div className="footer-contact-info">
                            <p><i className="fa-solid fa-envelope"></i> <a href="mailto:enquiries@envion.org.uk" style={{ color: 'white', textDecoration: 'none' }}>enquiries@envion.org.uk</a></p>
                        </div>
                    </div>
                    <div className="footer-column">
                        <h3>Solutions</h3>
                        <ul className="footer-links">
                            <li><Link to="/solutions#use-cases">Onboarding</Link></li>
                            <li><Link to="/solutions#use-cases">Preboarding</Link></li>
                        </ul>
                    </div>
                    <div className="footer-column">
                        <h3>Company</h3>
                        <ul className="footer-links">
                            <li><Link to="/about">About Us</Link></li>
                            <li><Link to="/contact">Contact</Link></li>
                        </ul>
                    </div>
                    <div className="footer-column">
                        <h3>Connect</h3>
                        <ul className="footer-social-links" style={{ listStyle: 'none', padding: 0, display: 'flex', gap: '16px', marginBottom: '24px' }}>
                            <li><a href="#" style={{ fontSize: '1.5rem', color: 'white', transition: 'opacity 0.3s' }} onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'} onMouseOut={(e) => e.currentTarget.style.opacity = '1'}><i className="fa-brands fa-instagram"></i></a></li>
                            <li><a href="#" style={{ fontSize: '1.5rem', color: 'white', transition: 'opacity 0.3s' }} onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'} onMouseOut={(e) => e.currentTarget.style.opacity = '1'}><i className="fa-brands fa-linkedin"></i></a></li>
                        </ul>
                        <div className="footer-compliance-badges" style={{ color: 'white', fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.4 }}>
                            <p style={{ marginBottom: '4px' }}>GDPR Compliant</p>
                            <p>Registered with ICO</p>
                        </div>
                    </div>
                </div>
                <div className="footer-bottom">
                    <div className="footer-bottom-grid">
                        <div className="footer-legal-info">
                            <p className="copyright">&copy; 2026 envion Inc. All rights reserved.</p>
                        </div>
                        <div className="footer-legal-links">
                            <Link to="/terms">Terms & Conditions</Link>
                            <Link to="/privacy">Privacy Policy</Link>
                            <Link to="/cookies">Cookies Policy</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default About;
